import React from 'react';
import { motion } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const milestones = [
  {
    year: '2005',
    title: 'First plotted community',
    detail: 'The studio began with a compact, detail-heavy residential cluster in Bangalore.',
  },
  {
    year: '2011',
    title: 'Shift into premium housing',
    detail: 'Layouts grew more private, more generous, and more attentive to arrival and circulation.',
  },
  {
    year: '2018',
    title: 'Commercial and mixed-use expansion',
    detail: 'Suraksha broadened into offices and urban mixed-format developments.',
  },
  {
    year: 'Today',
    title: 'Hospitality-led detailing',
    detail: 'Current projects balance long-term value with softer, more refined day-to-day living.',
  },
];

export default function About() {
  return (
    <section className="relative overflow-hidden pb-28 pt-36 md:pb-32 md:pt-44">
      <div className="floating-orb absolute right-[-8rem] top-24 h-72 w-72 rounded-full bg-accent/15" />
      <div className="floating-orb absolute left-[-10rem] bottom-12 h-80 w-80 rounded-full bg-primary/[0.06]" />

      <div className="section-shell relative">
        <div className="grid gap-14 lg:grid-cols-[minmax(0,0.95fr)_minmax(360px,1.05fr)] lg:items-center">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.7 }}
          >
            <span className="editorial-kicker">About the studio</span>
            <h1 className="mt-8 text-balance text-4xl leading-tight sm:text-5xl md:text-6xl">
              We build with a long memory for place and a disciplined approach to everyday living.
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-8 text-secondary/65">
              Suraksha Builders &amp; Developers grew by treating each address as a long-lived asset,
              not a quick sales surface. Our work brings together disciplined planning, durable material
              choices, and a quieter idea of luxury that gets better with time.
            </p>

            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              <div className="soft-panel p-6">
                <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-accent">What matters</p>
                <p className="mt-3 font-serif text-2xl text-secondary">Circulation, proportion, natural light, and privacy.</p>
              </div>
              <div className="soft-panel p-6">
                <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-accent">How we deliver</p>
                <p className="mt-3 font-serif text-2xl text-secondary">Through clear oversight, measured detailing, and consistent follow-through.</p>
              </div>
            </div>

            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-accent px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.28em] text-secondary transition-all duration-300 hover:bg-gold-light hover:text-primary"
            >
              Speak with the Team
              <ArrowRight size={16} />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.7, delay: 0.08 }}
            className="relative"
          >
            <div className="soft-panel overflow-hidden p-4 md:p-5">
              <div className="relative aspect-[4/5] overflow-hidden rounded-[1.75rem]">
                <img
                  src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80&w=1931"
                  alt="Suraksha architectural detail"
                  className="h-full w-full object-cover"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/50 via-transparent to-transparent" />
              </div>
            </div>

            <div className="dark-panel absolute bottom-6 left-6 hidden max-w-sm p-6 lg:block">
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-light/85">
                Studio note
              </p>
              <p className="mt-3 text-2xl font-serif text-secondary">
                Luxury should feel edited, easy to live with, and difficult to date.
              </p>
            </div>
          </motion.div>
        </div>

        <div className="mt-20 grid gap-5 lg:grid-cols-4">
          {milestones.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.55, delay: index * 0.05 }}
              className="soft-panel p-6"
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-accent">{item.year}</p>
              <h2 className="mt-3 text-2xl text-secondary">{item.title}</h2>
              <p className="mt-3 leading-7 text-secondary/62">{item.detail}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-20 grid gap-6 lg:grid-cols-2">
          <div className="soft-panel p-8 md:p-10">
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-accent">The vision</p>
            <h2 className="mt-4 text-3xl text-secondary md:text-4xl">Create places with permanence, not passing trends.</h2>
            <p className="mt-4 max-w-2xl text-base leading-8 text-secondary/62">
              Our projects are planned to age with dignity, to feel composed across decades, and to hold their value through restraint rather than spectacle.
            </p>
          </div>
          <div className="soft-panel p-8 md:p-10">
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-accent">The promise</p>
            <h2 className="mt-4 text-3xl text-secondary md:text-4xl">Remain exacting from concept sketch to handover.</h2>
            <p className="mt-4 max-w-2xl text-base leading-8 text-secondary/62">
              The team stays close to every stage, from planning decisions and site sequencing to finish selections and delivery communication.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
